import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton, TableSortLabel,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  PersonAdd, Edit, Search, Block, CheckCircle, LockReset,
  Visibility, VisibilityOff, AutoAwesome, ContentCopy,
} from '@mui/icons-material';
import apiClient from '../api/client';
import { AdminUser } from '../types';
import { useAuth } from '../contexts/AuthContext';

type SortField = 'display_name' | 'username' | 'email' | 'is_active' | 'last_login';
type SortDir = 'asc' | 'desc';

// ── Helpers ──────────────────────────────────────────────────

function generateUID(firstName: string, lastName: string): string {
  const first = firstName.trim().toLowerCase();
  const last = lastName.trim().toLowerCase();
  if (!first) return '';
  return last ? `${first}.${last}` : first;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '@#$!';
  let pwd = '';
  for (let i = 0; i < 8; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  pwd += specials[Math.floor(Math.random() * specials.length)];
  return pwd;
}

function splitName(fullName: string): { first: string; last: string } {
  const trimmed = (fullName || '').trim();
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1) };
}

function formatRelativeDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Component ────────────────────────────────────────────────

interface FormData {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  password: string;
}

const emptyForm: FormData = { first_name: '', last_name: '', username: '', email: '', password: '' };

export default function AdminAdminsPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('display_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Add/Edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Credential dialog (shown after create or reset password)
  const [credential, setCredential] = useState<{ username: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Deactivate dialog
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // ── Data fetching ──

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/admins');
      setAdmins(data);
    } catch (err) {
      console.error('Fetch admins error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // ── Sorting ──

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ── Filtering ──

  const filtered = useMemo(() => {
    let result = [...admins];

    if (statusFilter === 'active') result = result.filter(a => a.is_active);
    else if (statusFilter === 'inactive') result = result.filter(a => !a.is_active);

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        (a.display_name || '').toLowerCase().includes(q) ||
        a.username.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let aVal: string;
      let bVal: string;
      if (sortField === 'is_active') {
        aVal = a.is_active ? 'active' : 'inactive';
        bVal = b.is_active ? 'active' : 'inactive';
      } else if (sortField === 'last_login') {
        aVal = a.last_login || '';
        bVal = b.last_login || '';
      } else {
        aVal = ((a as any)[sortField] ?? '').toString().toLowerCase();
        bVal = ((b as any)[sortField] ?? '').toString().toLowerCase();
      }
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [admins, search, statusFilter, sortField, sortDir]);

  // ── Add/Edit form ──

  const isEdit = !!editAdmin;

  const handleOpenAdd = () => {
    setEditAdmin(null);
    const pwd = generatePassword();
    setForm({ ...emptyForm, password: pwd });
    setErrors({});
    setShowPassword(false);
    setFormOpen(true);
  };

  const handleOpenEdit = (admin: AdminUser) => {
    setEditAdmin(admin);
    const { first, last } = splitName(admin.display_name || admin.username);
    setForm({
      first_name: first,
      last_name: last,
      username: admin.username,
      email: admin.email,
      password: '',
    });
    setErrors({});
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditAdmin(null);
  };

  // Auto-generate username when first/last name changes (create mode only)
  const handleNameChange = (field: 'first_name' | 'last_name', value: string) => {
    const updated = { ...form, [field]: value };
    if (!isEdit) {
      updated.username = generateUID(
        field === 'first_name' ? value : form.first_name,
        field === 'last_name' ? value : form.last_name
      );
    }
    setForm(updated);
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Partial<Record<string, string>> = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    if (!form.username.trim()) errs.username = 'Username is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
    if (!isEdit && !form.password.trim()) errs.password = 'Password is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const displayName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      if (isEdit) {
        await apiClient.put(`/admins/${editAdmin!.id}`, {
          email: form.email,
          display_name: displayName,
        });
        setSnackbar({ message: 'Admin updated', severity: 'success' });
        setFormOpen(false);
        setEditAdmin(null);
      } else {
        await apiClient.post('/admins', {
          username: form.username,
          email: form.email,
          password: form.password,
          display_name: displayName,
        });
        setFormOpen(false);
        setCredential({ username: form.username, password: form.password });
      }
      fetchAdmins();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Operation failed';
      setSnackbar({ message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Credential dialog ──

  const handleCopyCredentials = () => {
    if (!credential) return;
    navigator.clipboard.writeText(`Username: ${credential.username}\nPassword: ${credential.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Reset Password ──

  const handleOpenReset = (admin: AdminUser) => {
    setResetTarget(admin);
    setResetPassword(generatePassword());
    setShowResetPassword(false);
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword) return;
    setResetting(true);
    try {
      await apiClient.post(`/admins/${resetTarget.id}/reset-password`, { password: resetPassword });
      setCredential({ username: resetTarget.username, password: resetPassword });
      setResetTarget(null);
      setSnackbar({ message: `Password reset for ${resetTarget.display_name || resetTarget.username}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to reset password', severity: 'error' });
    } finally {
      setResetting(false);
    }
  };

  // ── Deactivate / Reactivate ──

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await apiClient.delete(`/admins/${deactivateTarget.id}`);
      setSnackbar({ message: `${deactivateTarget.display_name || deactivateTarget.username} deactivated`, severity: 'success' });
      setDeactivateTarget(null);
      fetchAdmins();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to deactivate', severity: 'error' });
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (admin: AdminUser) => {
    try {
      await apiClient.put(`/admins/${admin.id}`, { is_active: true });
      setSnackbar({ message: `${admin.display_name || admin.username} reactivated`, severity: 'success' });
      fetchAdmins();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to reactivate', severity: 'error' });
    }
  };

  // ── Render ──

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const isMasterAdmin = (a: AdminUser) => a.username === 'admin';
  const isSelf = (a: AdminUser) => a.id === user?.id;

  const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];
  const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const SortHeader = ({ field, label, width }: { field: SortField; label: string; width?: number | string }) => (
    <TableCell sx={{ fontWeight: 600, width }}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Admins</Typography>
          <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.25 }}>Manage admin accounts and access</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={handleOpenAdd}
          sx={{ borderRadius: '10px', px: 2.5, py: 1 }}>
          Add Admin
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search name, username, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94A3B8' }} /></InputAdornment>,
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
          <ToggleButton value="active" sx={{ textTransform: 'none', px: 2 }}>Active</ToggleButton>
          <ToggleButton value="inactive" sx={{ textTransform: 'none', px: 2 }}>Inactive</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
            All Admins
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
            {filtered.length} admin{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader field="display_name" label="Name" />
              <SortHeader field="email" label="Email" width={220} />
              <SortHeader field="is_active" label="Status" width={110} />
              <SortHeader field="last_login" label="Last Login" width={110} />
              <TableCell sx={{ fontWeight: 600, width: 130 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {admins.length === 0 ? 'No admin users' : 'No admins match filters'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((admin) => (
                <TableRow key={admin.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        backgroundColor: `${getAvatarColor(admin.display_name || admin.username)}15`,
                        color: getAvatarColor(admin.display_name || admin.username),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(admin.display_name || admin.username)}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                            {admin.display_name || admin.username}
                          </Typography>
                          {isMasterAdmin(admin) && (
                            <Chip label="Master" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                          )}
                          {isSelf(admin) && (
                            <Chip label="You" size="small" color="info" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94A3B8', fontSize: 11 }}>
                          {admin.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={13}>{admin.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={admin.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={admin.is_active
                        ? { backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }
                        : { backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 500, fontSize: 11, height: 24 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={12} color="text.secondary">
                      {formatRelativeDate(admin.last_login)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => handleOpenEdit(admin)} title="Edit"
                        sx={{ color: '#94A3B8', '&:hover': { color: '#3B82F6', backgroundColor: '#EFF6FF' } }}>
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenReset(admin)} title="Reset Password"
                        sx={{ color: '#94A3B8', '&:hover': { color: '#8B5CF6', backgroundColor: '#F5F3FF' } }}>
                        <LockReset sx={{ fontSize: 18 }} />
                      </IconButton>
                      {!isMasterAdmin(admin) && !isSelf(admin) && (
                        admin.is_active ? (
                          <IconButton size="small" onClick={() => setDeactivateTarget(admin)} title="Deactivate"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#F59E0B', backgroundColor: '#FEF3C7' } }}>
                            <Block sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleReactivate(admin)} title="Reactivate"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#10B981', backgroundColor: '#D1FAE5' } }}>
                            <CheckCircle sx={{ fontSize: 18 }} />
                          </IconButton>
                        )
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* ── Add/Edit Admin Dialog ── */}
      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>{isEdit ? 'Edit Admin' : 'Add Admin'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => handleNameChange('first_name', e.target.value)}
              size="small"
              fullWidth
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
            <TextField
              label="Last Name"
              value={form.last_name}
              onChange={(e) => handleNameChange('last_name', e.target.value)}
              size="small"
              fullWidth
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
          </Box>
          <TextField
            label="Username (Login ID)"
            value={form.username}
            onChange={(e) => { setForm({ ...form, username: e.target.value }); setErrors({}); }}
            size="small"
            fullWidth
            disabled={isEdit}
            sx={{ mt: 2 }}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
            error={!!errors.username}
            helperText={errors.username}
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors({}); }}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            error={!!errors.email}
            helperText={errors.email}
          />
          {isEdit && editAdmin?.plain_password && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', mt: 2 }}>
              Current Password: <strong>{editAdmin.plain_password}</strong>
            </Typography>
          )}
          {!isEdit && (
            <TextField
              label="Password"
              value={form.password}
              onChange={(e) => { setForm({ ...form, password: e.target.value }); setErrors({}); }}
              size="small"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              sx={{ mt: 2 }}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => { setForm({ ...form, password: generatePassword() }); setShowPassword(true); }}
                      title="Generate password"
                      edge="end"
                    >
                      <AutoAwesome fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleFormClose} color="inherit" disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save' : 'Create Admin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Credential Dialog ── */}
      <Dialog open={!!credential} onClose={() => { setCredential(null); setCopied(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Admin Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share these credentials securely with the admin user.
          </DialogContentText>
          <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: '#f8fafc', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Username</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {credential?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Password</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {credential?.password}
                </Typography>
              </Box>
              <IconButton onClick={handleCopyCredentials} color={copied ? 'success' : 'default'} title="Copy to clipboard">
                <ContentCopy />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Copied to clipboard!
              </Typography>
            )}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setCredential(null); setCopied(false); }} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Password Dialog ── */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reset password for <strong>{resetTarget?.display_name || resetTarget?.username}</strong>
          </DialogContentText>
          <TextField
            label="New Password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            size="small"
            fullWidth
            type={showResetPassword ? 'text' : 'password'}
            InputProps={{
              sx: { fontFamily: 'monospace' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowResetPassword(!showResetPassword)} edge="end">
                    {showResetPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => { setResetPassword(generatePassword()); setShowResetPassword(true); }}
                    title="Generate password"
                    edge="end"
                  >
                    <AutoAwesome fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResetTarget(null)} color="inherit" disabled={resetting}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={!resetPassword || resetting}>
            {resetting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Deactivate Confirmation ── */}
      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Deactivate Admin</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to deactivate <strong>{deactivateTarget?.display_name || deactivateTarget?.username}</strong>?
            They will no longer be able to log in.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeactivateTarget(null)} color="inherit" disabled={deactivating}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" variant="contained" disabled={deactivating}>
            {deactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
